import React, { useState, useEffect } from 'react';
import { useMobile } from '../../hooks/useMobile';
import {
  useOnboardingContext,
  useSnackbarContext,
  useSideBarContext,
} from '../../contexts';
import { FEATURES_KEY } from '@8fig/ab-testing';
import { CustomTooltip } from '../BuildingBlocks/Tooltip/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import { useLocation, useHistory } from 'react-router';
import { useAuthContext } from '../../contexts/Auth';
import {
  pathname,
  getLogoSrc,
  getPublicFolderPath,
  is8DataApp,
} from '../../utils';
import messages from '../../messages';
import email from '../BuildingBlocks/Misc/emailSupportText';
import themeInfo from '../../themes/themes';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Link from '../misc/Link';
import IconButton from '@material-ui/core/IconButton';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import MenuIcon from '@material-ui/icons/Menu';
import { getInitNavItems, getInitNavItemsOther } from './navItems';

const useStyles = makeStyles((theme: any) => ({
  logoDiv: {
    marginLeft: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    height: 64,
  },
  logoDiv8data: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(1),
    height: 64,
  },
  drawer: {
    flexShrink: 0,
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(7) + 1,
    },
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  icon: { fontSize: 26, color: '#4d6575' },
  drawerPaper: {
    overflowX: 'hidden',
    width: theme.spacing(7) + 1,
    backgroundColor: theme.palette.background.dark,
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(7) + 1,
    },
  },
  divider: {
    background: theme.palette.lightGrey,
    opacity: 0.2,
  },
}));

export default function Sidebar() {
  const mobile = useMobile('sm');
  const location = useLocation();
  const history = useHistory();
  const snackbar = useSnackbarContext();
  const theme = process.env.REACT_APP_THEME;
  const logoSrc = getLogoSrc(themeInfo[theme], location.pathname);
  const classes = useStyles({
    logoHeight: themeInfo[theme].logoHeight,
    logoLeft: themeInfo[theme].logoLeft,
  });
  const { signOut } = useAuthContext();
  const { hasCompletedOnboarding } = useOnboardingContext();
  const { open, setOpen } = useSideBarContext();

  const initNavItems = getInitNavItems(location.pathname);
  const initNavItemsDifferentBehavior = getInitNavItemsOther();

  const [navItems, setNavItems] = useState(initNavItems);
  const [navItemsDifferentBehavior, setNavItemsDifferentBehavior] = useState(
    initNavItemsDifferentBehavior,
  );

  const is8Data = is8DataApp();

  useEffect(() => {
    let changedItemsList = navItems;
    changedItemsList.forEach((item) => {
      if (item.href === location.pathname) item.active = true;
      else item.active = false;
    });
    setNavItems([...changedItemsList]);
    // eslint-disable-next-line
  }, [location.pathname]);

  async function handleLogout() {
    try {
      // CLEARING LOCAL STORAGE FOR GOOGLE LOGOUT.
      // PER STACKOVERVFLOW, THERE IS A KNOWN BUG WHERE THIS IS REQUIRED
      // TO SUCCCESSFULLY LOG OUT THE USER.
      const abTests = localStorage.getItem(FEATURES_KEY);
      localStorage.clear();
      if (mobile) {
        setOpen(false);
      }
      await signOut();
      localStorage.setItem(FEATURES_KEY, abTests);
      history.push(pathname.preLogin.login.url);
    } catch (error) {
      snackbar.show(messages.auth.errors.signout, error);
    }
  }

  function onHoverEnter(text: string, differentBehavior: boolean) {
    if (differentBehavior) {
      let changedItemsList = navItemsDifferentBehavior;
      changedItemsList.forEach((item) => {
        if (item.text === text) item.hover = true;
      });
      setNavItemsDifferentBehavior([...changedItemsList]);
    } else {
      let changedItemsList = navItems;

      changedItemsList.forEach((item) => {
        if (item.text === text) item.hover = true;
      });
      setNavItems([...changedItemsList]);
    }
  }

  function onHoverLeave(text: string, differentBehavior: boolean) {
    if (differentBehavior) {
      let changedItemsList = navItemsDifferentBehavior;
      changedItemsList.forEach((item) => {
        if (item.text === text) item.hover = false;
      });
      setNavItemsDifferentBehavior([...changedItemsList]);
    } else {
      let changedItemsList = navItems;

      changedItemsList.forEach((item) => {
        if (item.text === text) item.hover = false;
      });
      setNavItems([...changedItemsList]);
    }
  }

  function showMobileMenuIcon() {
    if (open)
      return (
        <div className={classes.logoDiv}>
          <IconButton
            color="primary"
            aria-label="open drawer"
            edge="start"
            style={{ position: 'absolute', right: 5 }}
            className={classes.icon}
            onClick={() => {
              setOpen(false);
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </div>
      );
    else
      return (
        <div className={classes.logoDiv}>
          <IconButton
            color="primary"
            aria-label="open drawer"
            edge="start"
            onClick={() => {
              setOpen(true);
            }}
          >
            <MenuIcon />
          </IconButton>
        </div>
      );
  }

  return (
    <Drawer
      variant={mobile ? 'temporary' : 'permanent'}
      className={classes.drawer}
      classes={{
        paper: classes.drawerPaper,
      }}
      open={open}
      anchor="left"
      PaperProps={{ elevation: 4 }}
    >
      {mobile ? (
        showMobileMenuIcon()
      ) : (
        <div className={is8Data ? classes.logoDiv8data : classes.logoDiv}>
          <Link href="/" disabled={!hasCompletedOnboarding}>
            <img alt="logo" src={logoSrc} />
          </Link>
        </div>
      )}
      <Divider className={is8Data ? classes.divider : ''} />
      <List>
        {navItems.map(
          (
            { passiveLogo, hoverLogo, activeLogo, href, text, hover, active },
            index,
          ) => (
            <Link
              href={href}
              color="inherit"
              underline="none"
              key={index}
              disabled={!hasCompletedOnboarding}
            >
              <ListItem
                button
                disabled={!hasCompletedOnboarding}
                onClick={() => onHoverLeave(text, false)}
                onMouseEnter={() => onHoverEnter(text, false)}
                onMouseLeave={() => onHoverLeave(text, false)}
              >
                <ListItemIcon>
                  <CustomTooltip
                    arrow
                    open={hover}
                    title={text}
                    placement="top"
                  >
                    <div>
                      {active && <img src={activeLogo} alt={text} />}
                      {!active && (
                        <img
                          src={
                            getPublicFolderPath(window.location.pathname) +
                            (hover ? hoverLogo : passiveLogo)
                          }
                          alt={text}
                        />
                      )}
                    </div>
                  </CustomTooltip>
                </ListItemIcon>
              </ListItem>
            </Link>
          ),
        )}
        <a
          href={`mailto:${email.to}?subject=${email.subject.replace(
            ' ',
            '%20',
          )}&body=${email.body.replace(' ', '%20')}`}
          onMouseEnter={() =>
            onHoverEnter(navItemsDifferentBehavior[0]?.text, true)
          }
          onMouseLeave={() =>
            onHoverLeave(navItemsDifferentBehavior[0]?.text, true)
          }
        >
          <ListItem>
            <ListItemIcon>
              <CustomTooltip arrow title={'Support'} placement="top">
                <img
                  src={
                    getPublicFolderPath(window.location.pathname) +
                    (navItemsDifferentBehavior[0].hover
                      ? navItemsDifferentBehavior[0].hoverLogo
                      : navItemsDifferentBehavior[0].passiveLogo)
                  }
                  alt={navItemsDifferentBehavior[0].text}
                />
              </CustomTooltip>
            </ListItemIcon>
          </ListItem>
        </a>
        <Divider className={is8Data ? classes.divider : ''} />

        <ListItem
          button
          onClick={handleLogout}
          onMouseEnter={() =>
            onHoverEnter(navItemsDifferentBehavior[1]?.text, true)
          }
          onMouseLeave={() =>
            onHoverLeave(navItemsDifferentBehavior[1]?.text, true)
          }
        >
          <ListItemIcon>
            <CustomTooltip arrow title={'Sign out'} placement="top">
              <img
                src={
                  getPublicFolderPath(window.location.pathname) +
                  (navItemsDifferentBehavior[1].hover
                    ? navItemsDifferentBehavior[1].hoverLogo
                    : navItemsDifferentBehavior[1].passiveLogo)
                }
                alt={navItemsDifferentBehavior[1].text}
              />
            </CustomTooltip>
          </ListItemIcon>
        </ListItem>
      </List>
    </Drawer>
  );
}
